// Utilitaires pour la détection et l'extraction de liens

// Expressions régulières pour détecter les liens X/Twitter
export const X_URL_PATTERNS = [
  /https?:\/\/(www\.)?(twitter\.com|x\.com)\/\w+\/status\/\d+/g,
  /https?:\/\/(www\.)?(twitter\.com|x\.com)\/\w+\/status\/\d+\?\S*/g,
  /https?:\/\/(mobile\.)?(twitter\.com|x\.com)\/\w+\/status\/\d+/g
];

// Expressions régulières pour détecter les liens YouTube
export const YOUTUBE_URL_PATTERNS = [
  /https?:\/\/(www\.)?(youtube\.com|youtu\.be)\/watch\?v=([a-zA-Z0-9_-]{11})/g,
  /https?:\/\/(www\.)?youtu\.be\/([a-zA-Z0-9_-]{11})/g,
  /https?:\/\/(www\.)?(youtube\.com)\/embed\/([a-zA-Z0-9_-]{11})/g,
  /https?:\/\/(www\.)?(youtube\.com)\/v\/([a-zA-Z0-9_-]{11})/g,
  /https?:\/\/(m\.)?(youtube\.com)\/watch\?v=([a-zA-Z0-9_-]{11})/g
];

export const GENERAL_URL_PATTERN = /https?:\/\/[^\s]+/g;

/**
 * Détecte si le texte contient des liens X/Twitter
 */
export const detectXLinks = (text: string): string[] => {
  const links: string[] = [];
  
  X_URL_PATTERNS.forEach(pattern => {
    // Reset pattern pour éviter les problèmes de state
    pattern.lastIndex = 0;
    const matches = text.match(pattern);
    if (matches) {
      links.push(...matches);
    }
  });
  
  // Normaliser et supprimer les doublons basés sur l'ID du tweet
  const uniqueLinks = new Map<string, string>();
  
  links.forEach(link => {
    const tweetId = extractTweetId(link);
    if (tweetId && !uniqueLinks.has(tweetId)) {
      // Nettoyer l'URL en gardant seulement la partie essentielle
      const cleanUrl = link.split('?')[0]; // Supprimer les paramètres de requête
      uniqueLinks.set(tweetId, cleanUrl);
    }
  });
  
  return Array.from(uniqueLinks.values());
};

/**
 * Détecte si le texte contient des liens YouTube
 */
export const detectYouTubeLinks = (text: string): string[] => {
  const links: string[] = [];
  
  YOUTUBE_URL_PATTERNS.forEach(pattern => {
    pattern.lastIndex = 0;
    const matches = text.match(pattern);
    if (matches) {
      links.push(...matches);
    }
  });
  
  // Supprimer les doublons basés sur l'ID de la vidéo
  const uniqueLinks = new Map<string, string>();
  
  links.forEach(link => {
    const videoId = extractYouTubeVideoId(link);
    if (videoId && !uniqueLinks.has(videoId)) {
      uniqueLinks.set(videoId, link);
    }
  });
  
  return Array.from(uniqueLinks.values());
};

/**
 * Extrait l'ID du tweet à partir d'une URL X/Twitter
 */
export const extractTweetId = (url: string): string | null => {
  const match = url.match(/status\/(\d+)/);
  return match ? match[1] : null;
};

/**
 * Extrait l'ID de la vidéo YouTube à partir d'une URL
 */
export const extractYouTubeVideoId = (url: string): string | null => {
  // Pattern pour youtube.com/watch?v=
  let match = url.match(/[?&]v=([a-zA-Z0-9_-]{11})/);
  if (match) return match[1];
  
  // Pattern pour youtu.be/
  match = url.match(/youtu\.be\/([a-zA-Z0-9_-]{11})/);
  if (match) return match[1];
  
  // Pattern pour youtube.com/embed/
  match = url.match(/youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/);
  if (match) return match[1];
  
  // Pattern pour youtube.com/v/
  match = url.match(/youtube\.com\/v\/([a-zA-Z0-9_-]{11})/);
  if (match) return match[1];
  
  return null;
};

/**
 * Extrait le nom d'utilisateur à partir d'une URL X/Twitter
 */
export const extractXUsername = (url: string): string | null => {
  const match = url.match(/(?:twitter\.com|x\.com)\/(\w+)\/status/);
  return match ? match[1] : null;
};

/**
 * Détecte tous les liens dans un texte
 */
export const detectAllLinks = (text: string): string[] => {
  const matches = text.match(GENERAL_URL_PATTERN);
  return matches ? [...new Set(matches)] : [];
};

/**
 * Convertit une URL twitter.com en x.com ou vice versa
 */
export const normalizeXUrl = (url: string): string => {
  return url.replace(/twitter\.com/g, 'x.com');
};

/**
 * Vérifie si une URL est un lien X/Twitter valide
 */
export const isXUrl = (url: string): boolean => {
  return X_URL_PATTERNS.some(pattern => {
    // Créer une nouvelle instance du pattern pour éviter les problèmes d'état
    const newPattern = new RegExp(pattern.source, pattern.flags.replace('g', ''));
    return newPattern.test(url);
  });
};

/**
 * Vérifie si une URL est un lien YouTube valide
 */
export const isYouTubeUrl = (url: string): boolean => {
  return YOUTUBE_URL_PATTERNS.some(pattern => {
    const newPattern = new RegExp(pattern.source, pattern.flags.replace('g', ''));
    return newPattern.test(url);
  });
};
